/**
 * Lean project page for external well examiners: project header without
 * settings, audit or close actions and a single "Well examination" tab that
 * only allows listing, previewing, downloading and uploading.
 */
import { useContext, useMemo, useState } from "react";
import SharepointComponent from "../components/SharepointComponent";
import TabButton from "../components/TabButton";
import { createWellExaminationApi } from "../services/api-server/wellExamination";
import { useTenantStore } from "../stores/tenantStore";
import { TProjectData } from "../types/project";
import { EXAMINER_SHAREPOINT_ACTIONS } from "../types/sharepointApi";
import { GetAntIcon } from "../utils/ant_icons";
import { setLookupData } from "../utils/lookupFinder";
import { HomeContext } from "./Home";

const WELL_EXAMINATION_TAB = "well-examination";

const headerFields = [
	{ label: "Project title", key: "project_name" },
	{ label: "Project code", key: "project_code" },
	{ label: "Owner/Operator", key: "client" },
	{ label: "Project area", key: "project_area" },
];

function WellExaminationProjectView({
	projectData,
}: {
	projectData: TProjectData;
}) {
	const context: any = useContext(HomeContext);
	const tenantKey = useTenantStore((state) => state.tenant?.value);
	const [activePage, setActivePage] = useState<string>(WELL_EXAMINATION_TAB);

	const api = useMemo(
		() =>
			tenantKey && projectData?.project_code
				? createWellExaminationApi(tenantKey, projectData.project_code)
				: null,
		[tenantKey, projectData?.project_code]
	);

	if (!api) return null;

	return (
		<div
			className="generic-container"
			style={{ gap: "20px", overflow: "auto" }}
		>
			<div style={{ width: "100%" }}>
				<div className="container-header">
					<div className="panel-info">
						<span>Project details</span>
					</div>
				</div>
				<div
					className="details-content project-details-container"
					style={{ display: "flex", padding: "30px" }}
				>
					<div
						className="meta-data-container"
						style={{ gridTemplateColumns: "repeat(4, auto)" }}
					>
						{headerFields.map((field) => (
							<div
								key={field.key}
								style={{ display: "grid", gridTemplateRows: "20px auto" }}
							>
								<span className="panel-detail-label">{field.label}</span>
								<span className="panel-item-content">
									{setLookupData(context.allData, projectData, field.key)}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>

			<div
				className="main-container"
				style={{
					minHeight: "85%",
					flex: 1,
					width: "100%",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div className="project-header">
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<TabButton
							identifier={WELL_EXAMINATION_TAB}
							label="Well examination"
							icon={GetAntIcon("file2")}
							activePage={activePage}
							setActivePage={setActivePage}
						/>
					</div>
				</div>
				<div
					className="generic-content project-overview-container"
					style={{
						flex: 1,
						display: "flex",
						flexWrap: "inherit",
						flexDirection: "column",
						alignItems: "flex-start",
						padding: 0,
						gap: 0,
						overflowX: "auto",
						maxHeight: "100%",
					}}
				>
					<SharepointComponent
						type="well-examination"
						id={projectData.project_code}
						name={projectData.project_name}
						api={api}
						allowedActions={EXAMINER_SHAREPOINT_ACTIONS}
						setDisabled={() => {}}
						visible={activePage === WELL_EXAMINATION_TAB}
					/>
				</div>
			</div>
		</div>
	);
}

export default WellExaminationProjectView;
